import { getUnifiedModule } from '@/utilities/logger/amplitude/initialise';
import { syncAmplitudeIdentity } from '@/utilities/logger/amplitude/amplitudeIdentity';
import { buildCommonTrackingContext } from '@/utilities/logger/amplitude/tracking/buildCommonTrackingContext';
import { logTrackingEvent } from '@/utilities/logger/amplitude/tracking';
import {
  MODULES,
  ZONES,
  getAmplitudeKey,
} from '@/utilities/logger/amplitude/tracking/constants';
import {
  SNOWPLOW_EVENT_NAMES,
  sendEventToSnowplow,
} from '@/utilities/logger/snowplow/snowplow';
import { getGameLaunchSourceForCurrentSession } from '@/utilities/logger/snowplow/game-launch-source';

/**
 * Zone Ranking (ZR) Amplitude events fired during the game lifecycle.
 * Mirrors legacy launcher `ZR_AMPLITUDE_EVENTS`.
 */
export const ZR_AMPLITUDE_EVENTS = {
  GAME_LAUNCH: 'GL Game Launch',
  GAME_EXIT: 'GL Game Exit',
} as const;

/** @deprecated Use {@link ZR_AMPLITUDE_EVENTS} — kept for existing imports. */
export const GAME_LIFECYCLE_AMPLITUDE_EVENTS = ZR_AMPLITUDE_EVENTS;

const EVENT_CATEGORY = 'games';

export type GameLifecycleAnalyticsContext = {
  gameId: string | null;
  gameName: string | null;
  gameProvider: string | null;
  gamingSessionTrackingId?: string | null;
  isShortLinkLaunch?: boolean;
  region?: string;
  userId?: string | null;
};

let launchAnalyticsFired = false;
let exitAnalyticsFired = false;
let cachedGameLaunchSource: string | null = null;
let cachedIsShortLinkLaunch: boolean | null = null;

/** Test-only helper to reset module-level idempotency flags between tests. */
export const __resetGameLifecycleTrackingForTests = () => {
  launchAnalyticsFired = false;
  exitAnalyticsFired = false;
  cachedGameLaunchSource = null;
  cachedIsShortLinkLaunch = null;
};

const resolveSessionLaunchTracking = (isShortLinkLaunch: boolean) => {
  if (!cachedGameLaunchSource) {
    cachedGameLaunchSource =
      getGameLaunchSourceForCurrentSession(isShortLinkLaunch);
    cachedIsShortLinkLaunch = isShortLinkLaunch;
  }

  return {
    gameLaunchSource: cachedGameLaunchSource,
    isShortLinkLaunch: cachedIsShortLinkLaunch ?? false,
  };
};

const snowplowLifecycleEventProps = (
  context: GameLifecycleAnalyticsContext,
) => {
  const { gameLaunchSource, isShortLinkLaunch } = resolveSessionLaunchTracking(
    context.isShortLinkLaunch ?? false,
  );

  return {
    gamingSessionTrackingId: context.gamingSessionTrackingId,
    gameLaunchSource,
    isShortLinkLaunch,
    gameId: context.gameId,
    gameName: context.gameName,
    gameProvider: context.gameProvider,
    eventCategory: EVENT_CATEGORY,
  };
};

const trackAmplitudeLifecycleEvent = (
  eventName: string,
  gamingSessionTrackingId?: string | null,
): void => {
  logTrackingEvent({
    ...buildCommonTrackingContext(),
    [getAmplitudeKey('EVENT')]: eventName,
    [getAmplitudeKey('MODULE')]: MODULES.IN_GAME,
    [getAmplitudeKey('ZONE_NAME')]: ZONES.IN_GAME,
    [getAmplitudeKey('GAMING_SESSION_TRACKING_ID')]: gamingSessionTrackingId,
  });
};

/**
 * Amplitude + Snowplow `game_launch`. Idempotent — fires at most once per page
 * load (legacy parity for postMessage re-emits and duplicate success signals).
 */
export const trackGameLaunch = (
  context: GameLifecycleAnalyticsContext,
): void => {
  if (launchAnalyticsFired) {
    return;
  }
  launchAnalyticsFired = true;

  const syncIdentity = context.userId
    ? syncAmplitudeIdentity(context.userId)
    : Promise.resolve();

  void syncIdentity.then(() => {
    trackAmplitudeLifecycleEvent(
      ZR_AMPLITUDE_EVENTS.GAME_LAUNCH,
      context.gamingSessionTrackingId,
    );
  });

  sendEventToSnowplow({
    event: SNOWPLOW_EVENT_NAMES.GAME_LAUNCH,
    eventProps: snowplowLifecycleEventProps(context),
    region: context.region,
    userId: context.userId,
  });
};

/**
 * Amplitude + Snowplow `game_exit`, then flush Amplitude. Only fires after a
 * launch was tracked; idempotent for pagehide / duplicate exit triggers.
 */
export const trackGameExit = (context: GameLifecycleAnalyticsContext): void => {
  if (!launchAnalyticsFired || exitAnalyticsFired) {
    return;
  }
  exitAnalyticsFired = true;

  const syncIdentity = context.userId
    ? syncAmplitudeIdentity(context.userId)
    : Promise.resolve();

  void syncIdentity.then(() => {
    trackAmplitudeLifecycleEvent(
      ZR_AMPLITUDE_EVENTS.GAME_EXIT,
      context.gamingSessionTrackingId,
    );

    getUnifiedModule()
      .then((mod) => mod.flush())
      .catch(() => {
        /* swallow — analytics must never break the app */
      });
  });

  sendEventToSnowplow({
    event: SNOWPLOW_EVENT_NAMES.GAME_EXIT,
    eventProps: snowplowLifecycleEventProps(context),
    region: context.region,
    userId: context.userId,
  });
};
